import { useRef, useState, useEffect } from 'react'
import { useMutation } from 'react-query'
import { AI, ArrowRight } from 'almanac-ui/icons'
import { useModals } from 'almanac-ui/Modal'
import { Modal } from 'components/Wizards/Wizard/Modal'
import { Input } from 'almanac-ui/TextInputField/Input'
import { AiErrorModal } from 'components/AiErrorModal/AiErrorModal'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { ourUuid } from 'utils'
import { reportError } from 'utils/error'
import { Field } from '../Field/Field'
import { Form } from '../Form/Form'
import { OutlineEditor } from './OutlineEditor/OutlineEditor'
import { OutlineEditorHandle } from './OutlineEditor/Types'
import { generateIntroduction } from '../generateIntroduction'
import { generateOutline } from '../generateOutline'
import { generateTitles } from '../generateTitles'
import { TextFieldLoadingState } from 'components/Wizards/TextFieldLoadingState/TextFieldLoadingState'
import { OutlineLoadingState } from 'components/Wizards/OutlineLoadingState/OutlineLoadingState'
import { BlogPostLength, BlogPostWizardProps } from './Types'
import Styles from './BlogPostWizard.module.scss'
import { useSourceMaterials } from 'hooks/useSourceMaterials'
import { useGenerateContent } from 'components/Wizards/Wizard/useGenerateContent'
import { AlmanacFile, GeneratePrompt } from 'types'
import { useRootStore } from 'stores/RootStore'
import { CTA } from 'stores/OnboardingStore'
import { Header } from '../Wizard/Header/Header'
import { Blog } from 'almanac-ui/icons/blaze/32'
import { Button } from 'blaze-ui/Button'
import { Heading } from 'blaze-ui/Heading/Heading'
import { ResponsiveDesign } from 'screens/blaze/Home/components/ResponsiveDesign'
import { MobileHeader } from '../Wizard/Header/MobileHeader'
import { WizardProvider } from 'components/Wizards/Wizard/WizardContext'
import { Values } from './StepOne'

type Uuid = string
type Identifiable<T> = [Uuid, T]

/**
 * A streamlined version of BlogPostWizard that skips the initial steps
 * and starts directly with outline generation. Used when creating blog posts
 * from brand kit templates.
 */
const MinimalBlogPostWizard = ({
  initialValues = {},
  onSuccess,
  nodeSelection,
  articleId,
  layerId,
  seasonalPromotion,
  isTurnInto,
  children,
  schema,
}: BlogPostWizardProps) => {
  // Start with the outline step (only showing 2 steps total)
  const [currentStep, setCurrentStep] = useState(1)
  const workspaceId = useCurrentWorkspaceId()
  const { openModal, closeModal } = useModals()
  const { isTabletPortrait, isMobile } = useResponsiveDesign()
  const onboarding = useRootStore().onboardingStore

  // Pre-load states - start with loading for better UX
  const [isInitialLoading, setIsInitialLoading] = useState(true)

  // Pre-fill all form values from initialValues
  const [formValues] = useState<Values>({
    topic: initialValues.topic || '',
    goal: initialValues.goal || 'promotional',
    audience: initialValues.audience || '',
    blog_length: initialValues.blog_length || BlogPostLength.Medium,
    seo_words: initialValues.seo_words || [],
    call_to_action: initialValues.call_to_action || '',
    author_persona: initialValues.author_persona || '',
    product_description: initialValues.product_description || '',
    avoid: initialValues.avoid || '',
    additional_info: initialValues.additional_info || '',
    tone: initialValues.tone || [],
    brand_voice_id: initialValues.brand_voice_id,
    include_images: initialValues.include_images || false,
    promotion: initialValues.promotion || '',
    language: initialValues.language || '',
    project: initialValues.project,
    filename: '',
  })

  const [introduction, setIntroduction] = useState(initialValues.introduction || '')
  const [outline, setOutline] = useState(initialValues.outline || '')
  const [titleOptions, setTitleOptions] = useState<Array<Identifiable<string>>>([])
  const [selectedTitleId, setSelectedTitleId] = useState<Uuid>()
  const title = titleOptions.find(([uuid]) => uuid === selectedTitleId)?.[1] || ''

  const generateTitlesMutationAbortController = useRef<AbortController | null>(null)
  if (generateTitlesMutationAbortController.current === null)
    generateTitlesMutationAbortController.current = new AbortController()
  const generateTitlesMutation = useMutation(generateTitles)

  const generateIntroductionMutationAbortController = useRef<AbortController | null>(null)
  if (generateIntroductionMutationAbortController.current === null)
    generateIntroductionMutationAbortController.current = new AbortController()
  const generateIntroductionMutation = useMutation(generateIntroduction)

  const generateOutlineMutationAbortController = useRef<AbortController | null>(null)
  if (generateOutlineMutationAbortController.current === null)
    generateOutlineMutationAbortController.current = new AbortController()
  const generateOutlineMutation = useMutation(generateOutline)

  const { generateContent, generateDocMutation } = useGenerateContent({
    generationType: ChannelType.BlogPost,
    articleId,
    layerId,
    nodeSelection,
    promptLibrary: seasonalPromotion === 'black_friday' ? 'black_friday' : 'default',
    isTurnInto,
    onSuccess,
  })

  const outlineEditor = useRef<OutlineEditorHandle | null>(null)

  // Automatically generate titles when component mounts
  useEffect(() => {
    generateTitleOptions(formValues)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Watch for loading states and update isInitialLoading
  useEffect(() => {
    if (
      !generateTitlesMutation.isLoading &&
      !generateIntroductionMutation.isLoading &&
      !generateOutlineMutation.isLoading &&
      titleOptions.length > 0
    ) {
      setIsInitialLoading(false)
    }
  }, [
    generateTitlesMutation.isLoading,
    generateIntroductionMutation.isLoading,
    generateOutlineMutation.isLoading,
    titleOptions.length,
  ])

  const generateTitleOptions = async (payload: typeof formValues) => {
    setTitleOptions([])
    const prompt: GeneratePrompt = { ...payload, generation_type: ChannelType.BlogPost }
    generateTitlesMutation.mutate(
      {
        payload: {
          prompt,
          workspaceId: workspaceId,
        },
        signal: generateTitlesMutationAbortController.current?.signal,
      },
      {
        onSuccess(data) {
          const titleOptions: Array<Identifiable<string>> = data.map((t) => [ourUuid(), t])
          const firstTitleId = titleOptions[0][0]
          const firstTitle = titleOptions[0][1]

          setTitleOptions(titleOptions)
          setSelectedTitleId(firstTitleId)

          // Automatically start generating outline and intro using the first title directly
          // rather than waiting for state to update
          generateIntro(firstTitle)
          generateHeaders(firstTitle)
        },
        onError(error) {
          reportError(error)
          openModal(AiErrorModal, {
            onCancel: () => {
              closeModal()
            },
            onTryAgain: () => {
              closeModal()
              generateTitleOptions(payload)
            },
          })
        },
      },
    )
  }

  const generateIntro = async (providedTitle: string | null = null) => {
    setIntroduction('')
    const currentTitle = providedTitle || title
    const prompt: GeneratePrompt = { ...getPayload(currentTitle), generation_type: ChannelType.BlogPost }
    generateIntroductionMutation.mutate(
      {
        payload: {
          prompt,
          workspaceId: workspaceId,
          title: currentTitle,
        },
        signal: generateIntroductionMutationAbortController.current?.signal,
      },
      {
        onSuccess(data) {
          setIntroduction(data ?? '')
        },
        onError(error) {
          reportError(error)
          openModal(AiErrorModal, {
            onCancel: () => {
              closeModal()
            },
            onTryAgain: () => {
              closeModal()
              generateIntro()
            },
          })
        },
      },
    )
  }

  const generateHeaders = async (providedTitle: string | null = null) => {
    outlineEditor.current?.clear()
    setOutline('')
    const currentTitle = providedTitle || title
    const prompt: GeneratePrompt = { ...getPayload(), generation_type: ChannelType.BlogPost }
    generateOutlineMutation.mutate(
      {
        payload: {
          prompt,
          workspaceId: workspaceId,
          title: currentTitle,
        },
        signal: generateOutlineMutationAbortController.current?.signal,
      },
      {
        onSuccess: async (data) => {
          setOutline(data)
          outlineEditor.current?.replaceHTMLString(data)
        },
        onError(error) {
          openModal(AiErrorModal, {
            onCancel: () => {
              closeModal()
            },
            onTryAgain: () => {
              closeModal()
              generateHeaders()
            },
          })
          reportError(error)
        },
      },
    )
  }

  const sourceMaterials = useSourceMaterials()
  // Modified type signature to correctly type the providedTitle parameter
  const getPayload = (
    providedTitle?: string | null,
  ): Partial<GeneratePrompt & { filename?: string; project?: AlmanacFile }> => {
    const referenceText = (
      formValues.source_materials
        ? sourceMaterials?.data?.filter(({ id }) => formValues.source_materials?.includes(id)) || []
        : []
    )
      .map(({ content }) => content)
      .join('\n')

    const fields = {
      generation_type: ChannelType.BlogPost,
      title: providedTitle || title,
      introduction,
      ...formValues,
      reference_text: referenceText.length > 0 ? referenceText : undefined,
    }
    return {
      ...fields,
      project: formValues.project,
      format: outline,
    }
  }

  const submitStepOne = async (
    additionalPayload?: Partial<
      GeneratePrompt & { filename?: string; project?: AlmanacFile; articleId?: string | null }
    >,
  ) => {
    onboarding.markCTAAsCompleted(CTA.BlogPost)
    let payload = getPayload()
    return generateContent({ ...payload, ...additionalPayload })
  }

  return (
    <WizardProvider schema={schema}>
      <Modal.Root>
        <ResponsiveDesign.Desktop>
          <Header
            steps={isTabletPortrait ? ['Outline', 'Doc'] : ['Blog Outline', 'Generate Doc']}
            currentStep={currentStep}
            onStepPress={setCurrentStep}
            stepperVariant={seasonalPromotion === 'black_friday' ? 'yellow' : 'blue'}
            title="Blog Post"
            icon={Blog}
            previewUrl="https://res.cloudinary.com/almanac/image/upload/v1710183787/Home%20Card%20Previews/xfpedur3adlb5odloqfm.png"
          />
        </ResponsiveDesign.Desktop>
        <ResponsiveDesign.Mobile>
          <MobileHeader
            steps={['Outline', 'Doc']}
            currentStep={currentStep}
            onStepPress={setCurrentStep}
            title="Blog Post"
            icon={Blog}
          />
        </ResponsiveDesign.Mobile>

        {currentStep === 1 && (
          <Form.Root>
            <Form.Section>
              <Form.SectionContent>
                <Form.Fields className={Styles.fieldsWrapper}>
                  <Heading level={4} id="wizard-header">
                    We used your website to create a blog post for you!
                  </Heading>
                  <Field>
                    <div className={Styles.inputField}>
                      <div className={Styles.inputLabel}>Title</div>
                      {generateTitlesMutation.isLoading || isInitialLoading ? (
                        <TextFieldLoadingState />
                      ) : (
                        <Input
                          isTextarea
                          scrollable={false}
                          className={Styles.boldInput}
                          name="title"
                          value={title}
                          onChange={(e) => {
                            if (!selectedTitleId) return
                            setTitleOptions((oldTitleOptions) => {
                              return oldTitleOptions.map((t) => {
                                if (t[0] === selectedTitleId) return [selectedTitleId, e.target.value]
                                return t
                              })
                            })
                          }}
                        />
                      )}
                    </div>

                    <div className={Styles.inputField}>
                      <div className={Styles.inputLabel}>Introduction</div>
                      {generateIntroductionMutation.isLoading ||
                      (isInitialLoading && !generateIntroductionMutation.isSuccess) ? (
                        <TextFieldLoadingState />
                      ) : (
                        <>
                          <Input
                            isTextarea
                            scrollable={false}
                            rows={4}
                            name="introduction"
                            value={introduction}
                            onChange={(e) => setIntroduction(e.target.value)}
                          />
                          <div className={Styles.inputActions}>
                            <Button variant="tertiary" size="lg" frontIcon={AI} onPress={() => generateIntro()}>
                              Regenerate Intro
                            </Button>
                          </div>
                        </>
                      )}
                    </div>
                  </Field>

                  <Field
                    label="This is your blog's outline, with H2s and H3s for paragraph headers."
                    description={
                      <>
                        Press <kbd>enter</kbd> to add new H2s and H3s and <kbd>tab</kbd> <kbd>shift + tab</kbd> to
                        indent / unindent lines.
                      </>
                    }
                  >
                    {generateOutlineMutation.isLoading || (isInitialLoading && !generateOutlineMutation.isSuccess) ? (
                      <OutlineLoadingState />
                    ) : (
                      <>
                        <OutlineEditor
                          ref={outlineEditor}
                          aria-label="Outline"
                          className={Styles.outlineEditor}
                          initialHtmlString={outline}
                          onChange={setOutline}
                        />

                        <div className={Styles.inputActions}>
                          <Button variant="tertiary" size="lg" frontIcon={AI} onPress={() => generateHeaders()}>
                            Regenerate Headers
                          </Button>
                        </div>
                      </>
                    )}
                  </Field>
                </Form.Fields>
              </Form.SectionContent>
            </Form.Section>
            <Form.Actions isSticky>
              <Form.PrimaryActions>
                {children ? (
                  children({
                    submitForm: submitStepOne,
                    isSubmitting:
                      generateOutlineMutation.isLoading ||
                      generateIntroductionMutation.isLoading ||
                      generateDocMutation.isLoading,
                  })
                ) : (
                  <Button
                    size="lg"
                    endIcon={ArrowRight}
                    onPress={() => submitStepOne()}
                    isDisabled={
                      generateOutlineMutation.isLoading ||
                      generateIntroductionMutation.isLoading ||
                      generateDocMutation.isLoading
                    }
                    fullWidth={isMobile}
                  >
                    Generate Post
                  </Button>
                )}
              </Form.PrimaryActions>
            </Form.Actions>
          </Form.Root>
        )}
      </Modal.Root>
    </WizardProvider>
  )
}

export { MinimalBlogPostWizard }
